/**
 * File utility functions
 * Helper functions for file size formatting and validation
 */

/**
 * Format file size from bytes to human-readable format
 */
export function formatFileSize(bytes: number | string | null): string {
  if (!bytes) return 'Unknown size'
  
  const numBytes = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes
  if (isNaN(numBytes) || numBytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(numBytes) / Math.log(k))
  
  return `${parseFloat((numBytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`
}

/**
 * Validate file type
 * Allowed types: PDF, DOCX, Excel (.xlsx, .xls), TXT
 */
export function isValidFileType(file: File): boolean {
  const allowedExtensions = ['.pdf', '.docx', '.xlsx', '.xls', '.txt']
  const allowedMimeTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'text/plain',
  ]
  
  const fileName = file.name.toLowerCase()
  const fileExtension = fileName.substring(fileName.lastIndexOf('.'))
  
  // Check extension
  if (!allowedExtensions.includes(fileExtension)) {
    return false
  }
  
  // Check MIME type (if available)
  if (file.type && !allowedMimeTypes.includes(file.type)) {
    // Some browsers may not set MIME type correctly, so we also check extension
    // Only reject if both extension and MIME type are invalid
    return allowedExtensions.includes(fileExtension)
  }
  
  return true
}

/**
 * Validate file size (max 30MB)
 */
export function isValidFileSize(file: File): boolean {
  const maxSize = 30 * 1024 * 1024 // 30MB in bytes
  return file.size <= maxSize
}

/**
 * Get file type icon name based on file extension
 */
export function getFileTypeIcon(fileName: string): string {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
  
  switch (extension) {
    case '.pdf':
      return 'FileText'
    case '.docx':
      return 'FileText'
    case '.xlsx':
    case '.xls':
      return 'FileSpreadsheet'
    case '.txt':
      return 'FileText'
    default:
      return 'File'
  }
}

/**
 * Format date for display
 */
export function formatFileDate(dateString: string | null): string {
  if (!dateString) return 'Unknown date'
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return 'Unknown date'
  }
}

